import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ChevronLeft, ChevronRight, Edit, Trash2, Sparkles, Clock, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { countClientsRenewingWithin, type ClientPlanRenewalInput } from "@shared/utils/renewals";

// Form state for scheduling a new event
const initialScheduleForm = {
  title: "",
  date: "",
  startTime: "",
  endTime: "",
  type: "client",
  contact: "none",
  location: "",
  physicalLocation: "",
  description: "",
  reminderMinutes: "none",
  invitedContacts: [] as string[],
  invitedTeamMembers: [] as string[],
  manualInvites: ""
};

// Form state for editing an event
const initialEditForm = {
  title: "",
  date: "",
  startTime: "",
  endTime: "",
  type: "client",
  contact: "none",
  location: "",
  physicalLocation: "",
  description: "",
  reminderMinutes: "none",
  invitedContacts: [] as string[],
  invitedTeamMembers: [] as string[],
  manualInvites: ""
};

// Helper: calculate end time 1 hour after start time
function calculateEndTime(startTime: string): string {
  if (!startTime) return "";
  const [hours, minutes] = startTime.split(':').map(Number);
  const endHours = (hours + 1) % 24;
  return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// Helper: format address from client/prospect
function formatAddress(entity: any): string {
  if (!entity) return "";
  const parts = [
    entity.address1,
    entity.address2,
    entity.city,
    entity.state,
    entity.zipCode
  ].filter(Boolean);
  if (parts.length > 0) return parts.join(", ");
  return entity.address || "";
}

// Helper: convert UTC ISO string to local date string (YYYY-MM-DD)
function isoToLocalDate(iso: string) {
  const d = new Date(iso);
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}
// Helper: convert UTC ISO string to local time string (HH:MM)
function isoToLocalTime(iso: string) {
  const d = new Date(iso);
  const hour = d.getHours().toString().padStart(2, '0');
  const min = d.getMinutes().toString().padStart(2, '0');
  return `${hour}:${min}`;
}

type ExternalEvent = {
  id: string | number;
  title: string;
  startDate: string; // yyyy-MM-dd
  endDate?: string;  // yyyy-MM-dd
  description?: string;
  color?: 'blue' | 'green' | 'purple';
};

export function CalendarView({ 
  scheduleMeetingData, 
  allowScheduling = true,
  externalEvents = []
}: { 
  scheduleMeetingData?: any; 
  allowScheduling?: boolean;
  externalEvents?: ExternalEvent[];
}) {
  const [scheduleForm, setScheduleForm] = useState(initialScheduleForm);
  const [editForm, setEditForm] = useState(initialEditForm);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'month' | 'day'>('month');
  // Inline validation errors for the schedule / edit appointment dialogs
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>({});
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [deleteConfirmEvent, setDeleteConfirmEvent] = useState<any>(null);
  const [viewingEvent, setViewingEvent] = useState<any>(null);

  // Helper: open Schedule dialog manually. Normally starts with a clean form,
  // but when the panel was opened via a "Schedule Meeting" quick action the
  // prefill (contact/type/location) is preserved — the dialog no longer
  // auto-opens, so this click is how the user reaches the prefilled form.
  const openManualScheduleDialog = () => {
    if (!allowScheduling) return;
    setScheduleForm(f =>
      scheduleMeetingData
        ? {
            ...initialScheduleForm,
            contact: f.contact,
            type: f.type,
            physicalLocation: f.physicalLocation,
            invitedContacts: [],
            invitedTeamMembers: [],
            manualInvites: "",
          }
        : { ...initialScheduleForm, contact: "", invitedContacts: [], invitedTeamMembers: [], manualInvites: "" }
    );
    setEndTimeManuallySet(false);
    setScheduleErrors({});
    setIsScheduleDialogOpen(true);
  };

  // Fetch clients, prospects, team members, and appointments
  const { data: clients = [] } = useQuery<any[]>({
    queryKey: ["/api/clients"],
    enabled: allowScheduling,
  });
  const { data: prospects = [] } = useQuery<any[]>({
    queryKey: ["/api/prospects"],
    enabled: allowScheduling,
  });
  const { data: teamMembers = [] } = useQuery<any[]>({
    queryKey: ["/api/agency/team-members"],
    enabled: allowScheduling,
  });
  const { data: clientPlans = [] } = useQuery<ClientPlanRenewalInput[]>({
    queryKey: ["/api/clientplans"],
    enabled: allowScheduling,
  });
  const renewalsIn60Days = useMemo(
    () =>
      countClientsRenewingWithin(clientPlans, 60, {
        clientIds: new Set(clients.filter((c) => c.status === "active").map((c) => c.id)),
      }),
    [clientPlans, clients],
  );
  const queryClient = useQueryClient();

  // Track if end time was manually overridden
  const [endTimeManuallySet, setEndTimeManuallySet] = useState(false);

  // Refetch appointments when month changes (prepares for server-side filtering later)
  const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
  const { data: appointments = [], isLoading: isAppointmentsLoading, isFetching: isAppointmentsFetching } = useQuery<any[]>({
    queryKey: ["/api/appointments", monthKey],
    enabled: allowScheduling,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Map external events (e.g., PTO) to appointment-like objects (read-only)
  const mappedExternal = useMemo(() => {
    return (externalEvents || []).map((ev) => {
      // Use 09:00-18:00 local for display; day view shows by starting hour
      const start = new Date(`${ev.startDate}T09:00:00`);
      const end = new Date(`${(ev.endDate || ev.startDate)}T18:00:00`);
      return {
        id: `ext-${ev.id}`,
        title: ev.title,
        description: ev.description || '',
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        color: ev.color || 'purple',
        isExternal: true,
      } as any;
    });
  }, [externalEvents]);

  // Combine real appointments with any provided external events
  const combinedAppointments = useMemo(() => {
    const internal = allowScheduling ? (appointments as any[]) : [];
    return [...internal, ...mappedExternal];
  }, [appointments, mappedExternal]);

  // Mutations for appointments
  const createAppointmentMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", "/api/appointments", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appointments"] });
      setIsScheduleDialogOpen(false);
    },
  });

  const updateAppointmentMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return apiRequest("PATCH", `/api/appointments/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appointments"] });
      setIsEditDialogOpen(false);
      setEditingEvent(null);
    },
  });

  const deleteAppointmentMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/appointments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/appointments"] });
      setIsEditDialogOpen(false);
      setEditingEvent(null);
    },
  });

  // Type-safe access to client and prospect data
  const clientData = clients as Array<{ id: number; name: string; contactEmail?: string; primaryContactName?: string; address?: string; address1?: string; address2?: string; city?: string; state?: string; zipCode?: string }>;
  const prospectData = prospects as Array<{ id: number; name: string; contactEmail?: string; primaryContactName?: string; address?: string; address1?: string; address2?: string; city?: string; state?: string; zipCode?: string }>;

  // Prefill the schedule form when meeting data is provided. The panel slide-
  // out is enough context on its own — auto-opening the schedule dialog on top
  // of it doubled the UI (modal + panel), so the dialog now waits for the user
  // to click Schedule; the contact/type/location arrive prefilled.
  useEffect(() => {
    if (scheduleMeetingData) {
      setEndTimeManuallySet(false);
      const contactValue = `${scheduleMeetingData.type}-${scheduleMeetingData.id}`;
      // Preselect the related contact in the form and type, auto-populate location
      let physicalLocation = "";
      if (scheduleMeetingData.type === "client") {
        const client = clientData.find(c => c.id === scheduleMeetingData.id);
        if (client) physicalLocation = formatAddress(client);
      } else if (scheduleMeetingData.type === "prospect") {
        const prospect = prospectData.find(p => p.id === scheduleMeetingData.id);
        if (prospect) physicalLocation = formatAddress(prospect);
      }
      setScheduleForm(f => {
        const nextType = scheduleMeetingData.type || f.type;
        // Bail out when nothing would change — while the client/prospect
        // queries are loading their `[]` defaults are new references each
        // render, so an unconditional state update here loops the effect
        // ("maximum update depth exceeded").
        if (
          f.contact === contactValue &&
          f.type === nextType &&
          f.physicalLocation === physicalLocation
        ) {
          return f;
        }
        return {
          ...f,
          contact: contactValue,
          type: nextType,
          physicalLocation,
          invitedContacts: [],
          invitedTeamMembers: [],
          manualInvites: ""
        };
      });
    }
  }, [scheduleMeetingData, clientData, prospectData]);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleViewEvent = (event: any) => {
    setViewingEvent(event);
  };

  const handleEditEvent = (event: any) => {
    setEditingEvent(event);
    setEditErrors({});
    setIsEditDialogOpen(true);
  };

  const handleDeleteEvent = (event: any) => {
    setDeleteConfirmEvent(event);
  };

  const confirmDeleteEvent = () => {
    if (deleteConfirmEvent) {
      deleteAppointmentMutation.mutate(deleteConfirmEvent.id);
      setDeleteConfirmEvent(null);
    }
  };

  const handleContactSelection = (value: string) => {
    // Auto-populate physical location from selected client/prospect
    let physicalLocation = "";
    if (value && value !== "none") {
      const [type, idStr] = value.split("-");
      const id = parseInt(idStr);
      if (type === "client") {
        const client = clientData.find(c => c.id === id);
        if (client) physicalLocation = formatAddress(client);
      } else if (type === "prospect") {
        const prospect = prospectData.find(p => p.id === id);
        if (prospect) physicalLocation = formatAddress(prospect);
      }
    }
    setScheduleForm(f => ({ ...f, contact: value, physicalLocation, invitedContacts: [] }));
  };
  
  // Handle start time change - auto-set end time to 1 hour later unless manually overridden
  const handleStartTimeChange = (startTime: string) => {
    if (!endTimeManuallySet || !scheduleForm.endTime) {
      const endTime = calculateEndTime(startTime);
      setScheduleForm(f => ({ ...f, startTime, endTime }));
    } else {
      setScheduleForm(f => ({ ...f, startTime }));
    }
  };
  
  // Handle end time change - mark as manually set
  const handleEndTimeChange = (endTime: string) => {
    setEndTimeManuallySet(true);
    setScheduleForm(f => ({ ...f, endTime }));
  };
  
  // Get contacts for selected client/prospect
  const getAvailableContacts = () => {
    if (!scheduleForm.contact || scheduleForm.contact === "none") return [];
    const [type, idStr] = scheduleForm.contact.split("-");
    const id = parseInt(idStr);
    const contacts: { id: string; name: string; email: string }[] = [];
    
    if (type === "client") {
      const client = clientData.find(c => c.id === id);
      if (client) {
        if (client.contactEmail) {
          contacts.push({
            id: `client-primary-${id}`,
            name: client.primaryContactName || "Primary Contact",
            email: client.contactEmail
          });
        }
      }
    } else if (type === "prospect") {
      const prospect = prospectData.find(p => p.id === id);
      if (prospect) {
        if (prospect.contactEmail) {
          contacts.push({
            id: `prospect-primary-${id}`,
            name: prospect.primaryContactName || "Primary Contact",
            email: prospect.contactEmail
          });
        }
      }
    }
    return contacts;
  };
  
  const toggleContactInvite = (contactId: string) => {
    setScheduleForm(f => ({
      ...f,
      invitedContacts: f.invitedContacts.includes(contactId)
        ? f.invitedContacts.filter(id => id !== contactId)
        : [...f.invitedContacts, contactId]
    }));
  };
  
  const toggleTeamMemberInvite = (memberId: string) => {
    setScheduleForm(f => ({
      ...f,
      invitedTeamMembers: f.invitedTeamMembers.includes(memberId)
        ? f.invitedTeamMembers.filter(id => id !== memberId)
        : [...f.invitedTeamMembers, memberId]
    }));
  };

  // Edit form helpers
  const handleEditContactSelection = (value: string) => {
    let physicalLocation = editForm.physicalLocation;
    if (value && value !== "none") {
      const [type, idStr] = value.split("-");
      const id = parseInt(idStr);
      if (type === "client") {
        const client = clientData.find(c => c.id === id);
        if (client) physicalLocation = formatAddress(client);
      } else if (type === "prospect") {
        const prospect = prospectData.find(p => p.id === id);
        if (prospect) physicalLocation = formatAddress(prospect);
      }
    }
    setEditForm(f => ({ ...f, contact: value, physicalLocation, invitedContacts: [] }));
  };
  
  const getAvailableContactsForEdit = () => {
    if (!editForm.contact || editForm.contact === "none") return [];
    const [type, idStr] = editForm.contact.split("-");
    const id = parseInt(idStr);
    const contacts: { id: string; name: string; email: string }[] = [];
    
    if (type === "client") {
      const client = clientData.find(c => c.id === id);
      if (client && client.contactEmail) {
        contacts.push({
          id: `client-primary-${id}`,
          name: client.primaryContactName || "Primary Contact",
          email: client.contactEmail
        });
      }
    } else if (type === "prospect") {
      const prospect = prospectData.find(p => p.id === id);
      if (prospect && prospect.contactEmail) {
        contacts.push({
          id: `prospect-primary-${id}`,
          name: prospect.primaryContactName || "Primary Contact",
          email: prospect.contactEmail
        });
      }
    }
    return contacts;
  };
  
  const toggleEditContactInvite = (contactId: string) => {
    setEditForm(f => ({
      ...f,
      invitedContacts: f.invitedContacts.includes(contactId)
        ? f.invitedContacts.filter(id => id !== contactId)
        : [...f.invitedContacts, contactId]
    }));
  };
  
  const toggleEditTeamMemberInvite = (memberId: string) => {
    setEditForm(f => ({
      ...f,
      invitedTeamMembers: f.invitedTeamMembers.includes(memberId)
        ? f.invitedTeamMembers.filter(id => id !== memberId)
        : [...f.invitedTeamMembers, memberId]
    }));
  };

  // Use real appointments from backend
  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  // Compute appointments in the visible month for fast lookup/highlighting
  const monthAppointments = useMemo(() => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    return (combinedAppointments as any[]).filter((a) => {
      const s = new Date(a.startTime);
      return s >= start && s < end;
    });
  }, [combinedAppointments, currentDate]);

  const hasEvent = (day: number) => {
    return monthAppointments.some((a) => {
      const d = new Date(a.startTime);
      return d.getFullYear() === currentDate.getFullYear() &&
        d.getMonth() === currentDate.getMonth() &&
        d.getDate() === day;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = getFirstDayOfMonth(currentDate);
    const days = [] as JSX.Element[];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(
        <div
          key={day}
          className={cn(
            "h-12 w-full flex items-center justify-center text-sm cursor-pointer rounded-md hover:bg-gray-100",
            isToday(day) && "bg-[#1737A2] text-white hover:bg-[#1737A2]",
            hasEvent(day) && !isToday(day) && "bg-blue-100 text-blue-800",
            selectedDate?.getDate() === day && "ring-2 ring-[#1737A2]"
          )}
          onClick={() => {
            const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            setSelectedDate(newDate);
            setViewMode('day');
          }}
        >
          {day}
        </div>
      );
    }
    return days;
  };

  // Populate edit form when editingEvent changes, using local time
  useEffect(() => {
    if (editingEvent) {
      // Determine contact select value: 'client-<id>' or 'prospect-<id>' if present, else 'none'
      let contact = "none";
      if (editingEvent.prospectId) {
        contact = `prospect-${editingEvent.prospectId}`;
      } else if (editingEvent.clientId) {
        contact = `client-${editingEvent.clientId}`;
      } else if (editingEvent.contactId && editingEvent.contactType) {
        contact = `${editingEvent.contactType}-${editingEvent.contactId}`;
      }
      setEditForm({
        title: editingEvent.title || "",
        date: editingEvent.startTime ? isoToLocalDate(editingEvent.startTime) : "",
        startTime: editingEvent.startTime ? isoToLocalTime(editingEvent.startTime) : "",
        endTime: editingEvent.endTime ? isoToLocalTime(editingEvent.endTime) : "",
        type: editingEvent.type || "client",
        contact,
        location: editingEvent.location || "",
        physicalLocation: editingEvent.physicalLocation || "",
        description: editingEvent.description || "",
        reminderMinutes: editingEvent.reminderMinutes ? String(editingEvent.reminderMinutes) : "none",
        invitedContacts: editingEvent.invitedContacts || [],
        invitedTeamMembers: editingEvent.invitedTeamMembers || [],
        manualInvites: editingEvent.manualInvites || ""
      });
    }
  }, [editingEvent]);

  const getEventsForDate = (date: Date) => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();
    return (combinedAppointments as any[]).filter((event: any) => {
      const s = new Date(event.startTime);
      return s.getFullYear() === y && s.getMonth() === m && s.getDate() === d;
    });
  };

  // Required-field validation shared by the schedule and edit dialogs.
  const validateAppointmentForm = (f: {
    title: string;
    date: string;
    startTime: string;
    endTime: string;
  }): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!f.title.trim()) errors.title = "Title is required";
    if (!f.date) errors.date = "Date is required";
    if (!f.startTime) errors.startTime = "Start time is required";
    if (!f.endTime) errors.endTime = "End time is required";
    else if (f.startTime && f.endTime <= f.startTime)
      errors.endTime = "End time must be after start time";
    return errors;
  };

  // Add event handler
  const handleAddEvent = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateAppointmentForm(scheduleForm);
    setScheduleErrors(errors);
    if (Object.keys(errors).length > 0) return;
    // Parse contact field
    let contactId = null;
    let contactType = null;
    if (scheduleForm.contact && scheduleForm.contact !== 'none') {
      const [type, id] = scheduleForm.contact.split('-');
      contactType = type;
      contactId = parseInt(id);
    }
    // Parse local date and time as local, not UTC
    function toLocalISOString(dateStr: string, timeStr: string) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hour, minute] = timeStr.split(':').map(Number);
      // month is 0-based in JS Date
      const localDate = new Date(year, month - 1, day, hour, minute);
      return localDate.toISOString();
    }
    createAppointmentMutation.mutate({
      title: scheduleForm.title,
      startTime: toLocalISOString(scheduleForm.date, scheduleForm.startTime),
      endTime: toLocalISOString(scheduleForm.date, scheduleForm.endTime),
      type: scheduleForm.type,
      contactId: contactId,
      contactType: contactType,
      location: scheduleForm.location,
      physicalLocation: scheduleForm.physicalLocation,
      description: scheduleForm.description,
      reminderMinutes: scheduleForm.reminderMinutes === 'none' ? null : parseInt(scheduleForm.reminderMinutes),
      invitedContacts: scheduleForm.invitedContacts,
      invitedTeamMembers: scheduleForm.invitedTeamMembers,
      manualInvites: scheduleForm.manualInvites
    });
  };

  // Edit event handler
  const handleSaveEditEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEvent) return;
    const editValidation = validateAppointmentForm(editForm);
    setEditErrors(editValidation);
    if (Object.keys(editValidation).length > 0) return;
    // Parse contact field (same as handleAddEvent)
    let contactId = null;
    let contactType = null;
    if (editForm.contact && editForm.contact !== 'none') {
      const [type, id] = editForm.contact.split('-');
      contactType = type;
      contactId = parseInt(id);
    }
    // If 'none' is selected, explicitly set contactId and contactType to null
    if (!editForm.contact || editForm.contact === 'none') {
      contactId = null;
      contactType = null;
    }
    function toLocalISOString(dateStr: string, timeStr: string) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hour, minute] = timeStr.split(':').map(Number);
      const localDate = new Date(year, month - 1, day, hour, minute);
      return localDate.toISOString();
    }
    // If unlinking, also send prospectId and clientId as null for backend compatibility
    const unlinkFields = (!editForm.contact || editForm.contact === 'none')
      ? { prospectId: null, clientId: null }
      : {};
    updateAppointmentMutation.mutate({
      id: editingEvent.id,
      data: {
        title: editForm.title,
        startTime: toLocalISOString(editForm.date, editForm.startTime),
        endTime: toLocalISOString(editForm.date, editForm.endTime),
        type: editForm.type,
        contactId: contactId,
        contactType: contactType,
        location: editForm.location,
        physicalLocation: editForm.physicalLocation,
        description: editForm.description,
        reminderMinutes: editForm.reminderMinutes === 'none' ? null : parseInt(editForm.reminderMinutes),
        invitedContacts: editForm.invitedContacts,
        invitedTeamMembers: editForm.invitedTeamMembers,
        manualInvites: editForm.manualInvites,
        ...unlinkFields,
      },
    });
  };

  const renderDayView = () => {
    if (!selectedDate) return null;
    const dayEvents = getEventsForDate(selectedDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };
    const formatTime = (dt: string | Date) => new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    // Show each event only once: at its starting hour
    const getEventForHour = (hour: number) => {
      return dayEvents.find((event: any) => new Date(event.startTime).getHours() === hour);
    };
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode('month')}
            >
              ← Back to Month
            </Button>
            <h3 className="text-lg font-semibold">{formatDate(selectedDate)}</h3>
          </div>
          {allowScheduling && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsScheduleDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Schedule
            </Button>
          )}
        </div>

        {/* Brief list of today's events */}
        {dayEvents.length > 0 && (
          <Card>
            <CardContent className="p-3 space-y-2">
              {dayEvents.map((event: any) => (
                <div key={event.id} className="text-sm flex items-center justify-between">
                  <div className="truncate">
                    <span className="font-medium text-gray-900">{event.title}</span>
                    {event.description && (
                      <span className="text-gray-500 truncate block text-xs">{event.description}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 flex-shrink-0 ml-2">
                    {formatTime(event.startTime)} - {formatTime(event.endTime)}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="h-[500px]">
          <CardContent className="p-0 h-full overflow-y-auto">
            <div className="space-y-0">
              {hours.map(hour => {
                const event = getEventForHour(hour);
                const timeStr = `${hour.toString().padStart(2, '0')}:00`;
                return (
                  <div 
                    key={hour} 
                    className="flex border-b border-gray-100 dark:border-gray-700 min-h-[50px] hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="w-20 p-3 text-sm text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 flex-shrink-0">
                      {timeStr}
                    </div>
                    <div className="flex-1 p-3">
                      {event && (
                        <div 
                          className={cn(
                            "p-2 rounded-lg text-sm group cursor-pointer",
                            event.color === "blue" && "bg-blue-100 border-l-4 border-blue-500",
                            event.color === "green" && "bg-green-100 border-l-4 border-green-500",
                            event.color === "purple" && "bg-purple-100 border-l-4 border-purple-500"
                          )}
                          onClick={() => handleViewEvent(event)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{event.title}</p>
                              {event.description && (
                                <p className="text-xs text-gray-600 mt-1 truncate">{event.description}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                {formatTime(event.startTime)} - {formatTime(event.endTime)}
                              </p>
                              {event.location && event.location.trim() !== '' && (
                                <a 
                                  href={event.location} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-[#1737A2] hover:underline block mt-1"
                                >
                                  Join Meeting
                                </a>
                              )}
                            </div>
                            {!event.isExternal && (
                              <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={(e) => { e.stopPropagation(); handleEditEvent(event); }}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-white"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event); }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Schedule Dialog - Always available */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule Appointment</DialogTitle>
          </DialogHeader>
          <form className="space-y-4 pb-4" onSubmit={handleAddEvent}>
            <div className="space-y-2">
              <Label htmlFor="title">Title <span className="text-red-500">*</span></Label>
              <Input
                id="title"
                placeholder="Meeting title"
                value={scheduleForm.title}
                className={scheduleErrors.title ? "border-red-500 focus-visible:ring-red-500" : ""}
                onChange={e => {
                  setScheduleForm(f => ({ ...f, title: e.target.value }));
                  setScheduleErrors(({ title, ...rest }) => rest);
                }}
              />
              {scheduleErrors.title && <p className="text-xs text-red-600">{scheduleErrors.title}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Date <span className="text-red-500">*</span></Label>
              <Input
                id="date"
                type="date"
                value={scheduleForm.date}
                className={scheduleErrors.date ? "border-red-500 focus-visible:ring-red-500" : ""}
                onChange={e => {
                  setScheduleForm(f => ({ ...f, date: e.target.value }));
                  setScheduleErrors(({ date, ...rest }) => rest);
                }}
              />
              {scheduleErrors.date && <p className="text-xs text-red-600">{scheduleErrors.date}</p>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="startTime">Start Time <span className="text-red-500">*</span></Label>
                <Input
                  id="startTime"
                  type="time"
                  value={scheduleForm.startTime}
                  className={scheduleErrors.startTime ? "border-red-500 focus-visible:ring-red-500" : ""}
                  onChange={e => {
                    handleStartTimeChange(e.target.value);
                    setScheduleErrors(({ startTime, ...rest }) => rest);
                  }}
                />
                {scheduleErrors.startTime && <p className="text-xs text-red-600">{scheduleErrors.startTime}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime">End Time <span className="text-red-500">*</span></Label>
                <Input
                  id="endTime"
                  type="time"
                  value={scheduleForm.endTime}
                  className={scheduleErrors.endTime ? "border-red-500 focus-visible:ring-red-500" : ""}
                  onChange={e => {
                    handleEndTimeChange(e.target.value);
                    setScheduleErrors(({ endTime, ...rest }) => rest);
                  }}
                />
                {scheduleErrors.endTime && <p className="text-xs text-red-600">{scheduleErrors.endTime}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={scheduleForm.type} onValueChange={v => setScheduleForm(f => ({ ...f, type: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client Meeting</SelectItem>
                  <SelectItem value="prospect">Prospect Call</SelectItem>
                  <SelectItem value="internal">Internal Meeting</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact">Connect to Client/Prospect</Label>
              <Select value={scheduleForm.contact} onValueChange={handleContactSelection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select contact (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {clientData.map((client) => (
                    <SelectItem key={`client-${client.id}`} value={`client-${client.id}`}>
                      {client.name} (Client)
                    </SelectItem>
                  ))}
                  {prospectData.map((prospect) => (
                    <SelectItem key={`prospect-${prospect.id}`} value={`prospect-${prospect.id}`}>
                      {prospect.name} (Prospect)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="physicalLocation">Location</Label>
              <Input id="physicalLocation" placeholder="Office address or meeting location" value={scheduleForm.physicalLocation} onChange={e => setScheduleForm(f => ({ ...f, physicalLocation: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Virtual Meeting Link (Optional)</Label>
              <Input id="location" type="url" placeholder="https://zoom.us/j/... or https://meet.google.com/..." value={scheduleForm.location} onChange={e => setScheduleForm(f => ({ ...f, location: e.target.value }))} />
            </div>
            
            {/* Invite Contacts Section */}
            {getAvailableContacts().length > 0 && (
              <div className="space-y-2">
                <Label>Invite Contacts</Label>
                <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                  {getAvailableContacts().map(contact => (
                    <label key={contact.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={scheduleForm.invitedContacts.includes(contact.id)}
                        onChange={() => toggleContactInvite(contact.id)}
                        className="rounded border-gray-300"
                      />
                      <span>{contact.name}</span>
                      <span className="text-gray-500 text-xs">{contact.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Invite Agency Members Section */}
            {teamMembers.length > 0 && (
              <div className="space-y-2">
                <Label>Invite Team Members</Label>
                <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                  {teamMembers.map((member: any) => (
                    <label key={member.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={scheduleForm.invitedTeamMembers.includes(member.id)}
                        onChange={() => toggleTeamMemberInvite(member.id)}
                        className="rounded border-gray-300"
                      />
                      <span>{member.firstName} {member.lastName}</span>
                      <span className="text-gray-500 text-xs">{member.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Manual Invites */}
            <div className="space-y-2">
              <Label htmlFor="manualInvites">Additional Invites (emails, comma-separated)</Label>
              <Input 
                id="manualInvites" 
                placeholder="email1@example.com, email2@example.com" 
                value={scheduleForm.manualInvites} 
                onChange={e => setScheduleForm(f => ({ ...f, manualInvites: e.target.value }))} 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reminder">Reminder</Label>
              <Select value={scheduleForm.reminderMinutes} onValueChange={v => setScheduleForm(f => ({ ...f, reminderMinutes: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reminder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No reminder</SelectItem>
                  <SelectItem value="5">5 minutes before</SelectItem>
                  <SelectItem value="10">10 minutes before</SelectItem>
                  <SelectItem value="15">15 minutes before</SelectItem>
                  <SelectItem value="30">30 minutes before</SelectItem>
                  <SelectItem value="60">1 hour before</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" placeholder="Meeting details..." value={scheduleForm.description} onChange={e => setScheduleForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" type="button" onClick={() => setIsScheduleDialogOpen(false)} disabled={createAppointmentMutation.isPending}>
                Cancel
              </Button>
              <Button className="bg-[#1737A2] hover:bg-[#1737A2]/90" type="submit" disabled={createAppointmentMutation.isPending}>
                {createAppointmentMutation.isPending ? "Scheduling..." : "Schedule"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Edit Event Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Appointment</DialogTitle>
          </DialogHeader>
          {editingEvent && (
            <form className="space-y-4 pb-4" onSubmit={handleSaveEditEvent}>
              <div className="space-y-2">
                <Label htmlFor="editTitle">Title <span className="text-red-500">*</span></Label>
                <Input
                  id="editTitle"
                  value={editForm.title}
                  className={editErrors.title ? "border-red-500 focus-visible:ring-red-500" : ""}
                  onChange={e => {
                    setEditForm(f => ({ ...f, title: e.target.value }));
                    setEditErrors(({ title, ...rest }) => rest);
                  }}
                />
                {editErrors.title && <p className="text-xs text-red-600">{editErrors.title}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="editDate">Date <span className="text-red-500">*</span></Label>
                <Input
                  id="editDate"
                  type="date"
                  value={editForm.date}
                  className={editErrors.date ? "border-red-500 focus-visible:ring-red-500" : ""}
                  onChange={e => {
                    setEditForm(f => ({ ...f, date: e.target.value }));
                    setEditErrors(({ date, ...rest }) => rest);
                  }}
                />
                {editErrors.date && <p className="text-xs text-red-600">{editErrors.date}</p>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="editStartTime">Start Time <span className="text-red-500">*</span></Label>
                  <Input
                    id="editStartTime"
                    type="time"
                    value={editForm.startTime}
                    className={editErrors.startTime ? "border-red-500 focus-visible:ring-red-500" : ""}
                    onChange={e => {
                      setEditForm(f => ({ ...f, startTime: e.target.value }));
                      setEditErrors(({ startTime, ...rest }) => rest);
                    }}
                  />
                  {editErrors.startTime && <p className="text-xs text-red-600">{editErrors.startTime}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editEndTime">End Time <span className="text-red-500">*</span></Label>
                  <Input
                    id="editEndTime"
                    type="time"
                    value={editForm.endTime}
                    className={editErrors.endTime ? "border-red-500 focus-visible:ring-red-500" : ""}
                    onChange={e => {
                      setEditForm(f => ({ ...f, endTime: e.target.value }));
                      setEditErrors(({ endTime, ...rest }) => rest);
                    }}
                  />
                  {editErrors.endTime && <p className="text-xs text-red-600">{editErrors.endTime}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editType">Type</Label>
                <Select value={editForm.type} onValueChange={v => setEditForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client Meeting</SelectItem>
                    <SelectItem value="prospect">Prospect Call</SelectItem>
                    <SelectItem value="internal">Internal Meeting</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editContact">Connect to Client/Prospect</Label>
                <Select value={editForm.contact} onValueChange={handleEditContactSelection}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {clientData.map((client) => (
                      <SelectItem key={`client-${client.id}`} value={`client-${client.id}`}>
                        {client.name} (Client)
                      </SelectItem>
                    ))}
                    {prospectData.map((prospect) => (
                      <SelectItem key={`prospect-${prospect.id}`} value={`prospect-${prospect.id}`}>
                        {prospect.name} (Prospect)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editPhysicalLocation">Location</Label>
                <Input id="editPhysicalLocation" placeholder="Office address or meeting location" value={editForm.physicalLocation} onChange={e => setEditForm(f => ({ ...f, physicalLocation: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editLocation">Virtual Meeting Link (Optional)</Label>
                <Input id="editLocation" type="url" value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} placeholder="https://zoom.us/j/... or https://meet.google.com/..." />
              </div>
              
              {/* Invite Contacts Section */}
              {getAvailableContactsForEdit().length > 0 && (
                <div className="space-y-2">
                  <Label>Invite Contacts</Label>
                  <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                    {getAvailableContactsForEdit().map(contact => (
                      <label key={contact.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={editForm.invitedContacts.includes(contact.id)}
                          onChange={() => toggleEditContactInvite(contact.id)}
                          className="rounded border-gray-300"
                        />
                        <span>{contact.name}</span>
                        <span className="text-gray-500 text-xs">{contact.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Invite Agency Members Section */}
              {teamMembers.length > 0 && (
                <div className="space-y-2">
                  <Label>Invite Team Members</Label>
                  <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                    {teamMembers.map((member: any) => (
                      <label key={member.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={editForm.invitedTeamMembers.includes(member.id)}
                          onChange={() => toggleEditTeamMemberInvite(member.id)}
                          className="rounded border-gray-300"
                        />
                        <span>{member.firstName} {member.lastName}</span>
                        <span className="text-gray-500 text-xs">{member.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Manual Invites */}
              <div className="space-y-2">
                <Label htmlFor="editManualInvites">Additional Invites (emails, comma-separated)</Label>
                <Input 
                  id="editManualInvites" 
                  placeholder="email1@example.com, email2@example.com" 
                  value={editForm.manualInvites} 
                  onChange={e => setEditForm(f => ({ ...f, manualInvites: e.target.value }))} 
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="editReminder">Reminder</Label>
                <Select value={editForm.reminderMinutes} onValueChange={v => setEditForm(f => ({ ...f, reminderMinutes: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reminder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No reminder</SelectItem>
                    <SelectItem value="5">5 minutes before</SelectItem>
                    <SelectItem value="10">10 minutes before</SelectItem>
                    <SelectItem value="15">15 minutes before</SelectItem>
                    <SelectItem value="30">30 minutes before</SelectItem>
                    <SelectItem value="60">1 hour before</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editDescription">Description</Label>
                <Textarea id="editDescription" value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} placeholder="Meeting details..." />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" type="button" onClick={() => setIsEditDialogOpen(false)} disabled={updateAppointmentMutation.isPending}>
                  Cancel
                </Button>
                <Button className="bg-[#1737A2] hover:bg-[#1737A2]/90" type="submit" disabled={updateAppointmentMutation.isPending}>
                  {updateAppointmentMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmEvent} onOpenChange={(open) => !open && setDeleteConfirmEvent(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Appointment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirmEvent?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setDeleteConfirmEvent(null)} disabled={deleteAppointmentMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteEvent} disabled={deleteAppointmentMutation.isPending}>
              {deleteAppointmentMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Details Dialog */}
      <Dialog open={!!viewingEvent} onOpenChange={(open) => !open && setViewingEvent(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#1737A2]" />
              {viewingEvent?.title}
            </DialogTitle>
          </DialogHeader>
          {viewingEvent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Date</p>
                  <p className="text-sm">{new Date(viewingEvent.startTime).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Time</p>
                  <p className="text-sm">{new Date(viewingEvent.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(viewingEvent.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-500">Type</p>
                <p className="text-sm capitalize">{viewingEvent.type === 'client' ? 'Client Meeting' : viewingEvent.type === 'prospect' ? 'Prospect Call' : viewingEvent.type === 'internal' ? 'Internal Meeting' : 'Other'}</p>
              </div>
              
              {viewingEvent.physicalLocation && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Location</p>
                  <p className="text-sm">{viewingEvent.physicalLocation}</p>
                </div>
              )}
              
              {viewingEvent.location && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Virtual Meeting Link</p>
                  <a href={viewingEvent.location} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1737A2] hover:underline">{viewingEvent.location}</a>
                </div>
              )}
              
              {viewingEvent.description && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Description</p>
                  <p className="text-sm whitespace-pre-wrap">{viewingEvent.description}</p>
                </div>
              )}
              
              {(viewingEvent.invitedContacts?.length > 0 || viewingEvent.invitedTeamMembers?.length > 0 || viewingEvent.manualInvites) && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Invitees</p>
                  <div className="text-sm space-y-1 mt-1">
                    {viewingEvent.invitedContacts?.map((id: string) => (
                      <p key={id} className="text-gray-600">Contact: {id}</p>
                    ))}
                    {viewingEvent.invitedTeamMembers?.map((id: string) => {
                      const member = teamMembers.find((m: any) => m.id === id);
                      return <p key={id} className="text-gray-600">{member ? `${member.firstName} ${member.lastName}` : id}</p>;
                    })}
                    {viewingEvent.manualInvites && <p className="text-gray-600">{viewingEvent.manualInvites}</p>}
                  </div>
                </div>
              )}
              
              {viewingEvent.reminderMinutes && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Reminder</p>
                  <p className="text-sm">{viewingEvent.reminderMinutes === 60 ? '1 hour' : `${viewingEvent.reminderMinutes} minutes`} before</p>
                </div>
              )}
              
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setViewingEvent(null)}>
                  Close
                </Button>
                {!viewingEvent.isExternal && (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setViewingEvent(null);
                        handleEditEvent(viewingEvent);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={() => {
                        setViewingEvent(null);
                        handleDeleteEvent(viewingEvent);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {viewMode === 'month' ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Calendar */}
          <div className="lg:col-span-9">
            <Card className="h-[500px]">
              <CardContent className="p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h4 className="text-lg font-semibold">
                      {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h4>
                    {isAppointmentsFetching && (
                      <span className="text-xs text-gray-500 animate-pulse">Loading...</span>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    <Button variant="outline" size="sm" onClick={previousMonth}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={nextMonth}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className="h-10 flex items-center justify-center text-xs font-medium text-gray-500">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2 flex-1">
                  {renderCalendar()}
                </div>
              </CardContent>
            </Card>
          </div>
          {/* Right Column: Upcoming Events + AI Suggestions stacked */}
          <div className="lg:col-span-3 space-y-6">
            {/* Upcoming Events */}
            <div className="space-y-3">
              {allowScheduling && viewMode === 'month' && (
                <div className="flex justify-end mb-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openManualScheduleDialog}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Schedule
                  </Button>
                </div>
              )}
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Upcoming Events
              </h4>
              {(combinedAppointments as any[])
                .filter((event: any) => new Date(event.startTime) > new Date())
                .sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                .slice(0, 4)
                .map((event: any) => (
                  <Card key={event.id} className="group cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleViewEvent(event)}>
                    <CardContent className="p-3">
                      <div className="flex items-start space-x-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{event.title}</p>
                          <p className="text-xs text-gray-500 truncate">{event.description}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(event.startTime).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {' - '}
                            {new Date(event.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {event.location && event.location.trim() !== '' && (
                            <a 
                              href={event.location} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-[#1737A2] hover:underline block mt-1 truncate"
                            >
                              Join Meeting
                            </a>
                          )}
                        </div>
                        {!event.isExternal && (
                          <div className="flex flex-col space-y-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => { e.stopPropagation(); handleEditEvent(event); }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:text-white"
                              onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event); }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              {(combinedAppointments as any[]).filter((event: any) => new Date(event.startTime) > new Date()).length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No upcoming events</p>
              )}
            </div>
            
            {/* AI Suggestions */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />
                AI Suggestions
              </h4>
              {/* DEV-332: static prospect/quote cards removed (fixed fake counts); renewals count is real */}
              <Card className="border-orange-100 bg-orange-50/50">
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Schedule renewals</p>
                      <p className="text-xs text-gray-600 mt-1" data-testid="calendar-renewals-60">
                        {renewalsIn60Days === 0
                          ? "No client renewals in the next 60 days"
                          : `${renewalsIn60Days} client renewal${renewalsIn60Days === 1 ? " is" : "s are"} coming up in the next 60 days`}
                      </p>
                      <Button variant="link" size="sm" className="h-auto p-0 text-xs text-orange-600 mt-1">
                        View renewals
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        renderDayView()
      )}
    </div>
  );
}
